import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { t } from '../../utils/translations';
import { ShoppingListProduct } from '../../types/shoppingList';

interface EditProductPanelProps {
  open: boolean;
  onClose: () => void;
  product: ShoppingListProduct | null;
  onSave: (quantity: number, comment?: string) => Promise<void>;
}

const EditProductPanel: React.FC<EditProductPanelProps> = ({ open, onClose, product, onSave }) => {
  const [quantity, setQuantity] = useState<string>('1');
  const [comment, setComment] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    if (product) {
      setQuantity(product.quantity.toString());
      setComment(product.comment || '');
    }
  }, [product]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const quantityNum = parseInt(quantity, 10);
    if (isNaN(quantityNum) || quantityNum <= 0) {
      return;
    }

    try {
      setSaving(true);
      await onSave(quantityNum, comment.trim() || undefined);
      handleClose();
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setQuantity('1');
    setComment('');
    onClose();
  };

  if (!product) return null;

  return (
    <Drawer anchor="bottom" open={open} onClose={handleClose}>
      <Box sx={{ padding: 3, minHeight: '350px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
          <Typography variant="h6">{t('shoppingLists.editProduct')}</Typography>
          <IconButton onClick={handleClose} aria-label={t('common.close')}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            label={t('shoppingLists.productName')}
            value={product.name}
            disabled
            fullWidth
            sx={{ marginBottom: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', marginBottom: 2 }}>
            <TextField
              label={t('shoppingLists.quantity')}
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              required
              disabled={saving}
              inputProps={{ min: 1 }}
              sx={{ flex: 1 }}
            />
            <Typography variant="body1" sx={{ minWidth: '100px', color: 'text.secondary' }}>
              {t(`units.${product.unit.toLowerCase()}`)}
            </Typography>
          </Box>

          <TextField
            label={t('shoppingLists.comment')}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            fullWidth
            multiline
            rows={3}
            disabled={saving}
            placeholder={t('shoppingLists.commentPlaceholder')}
            sx={{ marginBottom: 3 }}
          />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
            <Button onClick={handleClose} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : null}
            >
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default EditProductPanel;
